interface ListPagerProps {
  /** 1-based page being shown; clamped into range here. */
  page: number;
  pageSize: number;
  /** Items across ALL pages — never just the page's own slice. */
  total: number;
  onPageChange: (page: number) => void;
  /** Plural noun for the count line, e.g. "cameras". */
  itemLabel?: string;
  className?: string;
  /** In a card's footer (which draws its own rule and padding): no top border or padding. */
  bare?: boolean;
}

/** Page numbers to show around `current`, with '...' gaps — shared by ListPager and Pagination. */
export function pageWindow(current: number, totalPages: number, delta = 2): (number | '...')[] {
  const pages: (number | '...')[] = [];
  const left = Math.max(1, current - delta);
  const right = Math.min(totalPages, current + delta);
  if (left > 1) pages.push(1);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push('...');
  if (right < totalPages) pages.push(totalPages);
  return pages;
}

/** A pager arrow button (← Prev / Next →) — for a prev/next-only pager. */
export const PAGER_NAV_BTN = 'rounded-lg border border-border px-2.5 py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40';

/** The app's one set of page controls: ← Prev, numbered pages (current in brand), Next →. */
export function PagerNav({ current, pages, totalPages, onPageChange }: {
  current: number; pages: (number | '...')[]; totalPages: number; onPageChange: (page: number) => void;
}) {
  return (
    <nav aria-label="Pagination" className="flex items-center gap-1">
      <button type="button" onClick={() => onPageChange(current - 1)} disabled={current <= 1} className={PAGER_NAV_BTN}>← Prev</button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`e${i}`} className="px-1.5 text-[12px] text-muted-foreground">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            aria-current={p === current ? 'page' : undefined}
            className={`min-w-[2rem] rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
              p === current ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {p}
          </button>
        ),
      )}
      <button type="button" onClick={() => onPageChange(current + 1)} disabled={current >= totalPages} className={PAGER_NAV_BTN}>Next →</button>
    </nav>
  );
}

/**
 * The bar under a client-side paginated list — the camera grid, tree and table on both the
 * local and cloud Camera List use this one, so the bar looks and sits the same everywhere.
 * The count always shows (even on a single page); the page controls only when there is
 * somewhere to go.
 */
export default function ListPager({ page, pageSize, total, onPageChange, itemLabel = 'items', className = '', bare = false }: ListPagerProps) {
  if (total <= 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-2 ${bare ? '' : 'pt-3 border-t border-border'} ${className}`}>
      <p className="text-[12px] text-muted-foreground">
        Page <span className="font-semibold text-foreground">{current}</span> of <span className="font-semibold text-foreground">{totalPages}</span> • Showing {start}–{end} of {total} {itemLabel}
      </p>
      {totalPages > 1 && <PagerNav current={current} pages={pageWindow(current, totalPages)} totalPages={totalPages} onPageChange={onPageChange} />}
    </div>
  );
}
